import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';

import { updateFlowStateTopicIds } from '@api/narrative-projects';
import { mapFlowStateComponentsToDrafts } from '@api/narrative-projects/utilities/helpers';
import {
  createNarrative,
  readDraftTopics,
  reorderFlowTopics,
  softDeleteFlowTopic,
  updateTopicVisibility,
} from '@api/narratives';
import { Button, ModalV2 as Modal, toast } from '@utilities';

import Footer from '../../../../../../components/Footer';
import PreBuildModal from '../../../../components/PreBuildModal';
import NarrativeFlowView from '../../../../components/NarrativeFlowView';
import NarrativeTitle from '../../../../components/NarrativeFlowView/components/NarrativeTitle';
import NarrativeTopicCard from '../../../../components/NarrativeFlowView/components/NarrativeTopicCard';
import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../../Narratives/utilities/helpers';

import CanvasFloatingToolbar from './components/CanvasFloatingToolbar';
import {
  buildEnrichedDraftTopics,
  buildManualSelectedDrafts,
  buildPromptAnswersPayload,
  extractComponentIds,
  mergeDraftTopics,
  mergeUniqueValues,
  pruneDraftDatasetMap,
} from './utilities/helpers';
import {
  COMPLETED_OR_ERROR_FLOW_STATUSES,
  DEFAULT_PROMPT_MODAL_NAME,
  DEFAULT_PROMPT_ROUTE_LABEL,
  TOPIC_CARD_MOTION_PROPS,
} from './utilities/constants';

import {
  areRequiredPromptsFilled,
  buildDraftFlowData,
  buildDraftTopicData,
  buildEditablePromptsFromFlowState,
  buildTopicShell,
  EMPTY_DOWNLOAD_MAP,
  extractEditablePromptAnswers,
  hasPromptRequirements,
} from '../../utilities/helpers';

import styles from './_index.module.scss';

const NarrativesCanvas = ({
  chatSessionId,
  flowId,
  flowState,
  onFlowSelected,
  onFlowStateChange,
  onSelectedTopicsChange,
  selectedTopicCards = [],
}) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [draftDatasetMap, setDraftDatasetMap] = useState({});
  const [isBuilding, setIsBuilding] = useState(false);
  const [pendingDraftAction, setPendingDraftAction] = useState(null);
  const [pendingRunTopicAction, setPendingRunTopicAction] = useState(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [promptModalName, setPromptModalName] = useState(DEFAULT_PROMPT_MODAL_NAME);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});
  const [topicPendingDeletion, setTopicPendingDeletion] = useState(null);

  const promptSelectionsSnapshotRef = useRef(null);
  const shouldPersistPromptChangesRef = useRef(false);

  const prompts = useMemo(() => buildEditablePromptsFromFlowState(flowState), [flowState]);
  const canSelectPrompts = hasPromptRequirements(flowState) && prompts.length > 0;
  const isViewingRun = Boolean(flowId);
  const isViewingDraft = Boolean(flowState) && !isViewingRun;

  const narrativeFlow = useSelector((state) => {
    if (!flowId) return null;

    const flows = state?.websocket?.flows;
    if (!Array.isArray(flows)) return null;

    return flows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  });
  const userPermissions = useSelector((state) => state?.user?.data?.permissions) || {};
  const canManageRunTopics = Boolean(userPermissions?.canAccessChat);

  const flowStateDraftTopics = useMemo(
    () => (flowState ? mapFlowStateComponentsToDrafts(flowState) : []),
    [flowState]
  );

  const manualSelectedDrafts = useMemo(
    () => buildManualSelectedDrafts(selectedTopicCards),
    [selectedTopicCards]
  );
  const manualTopicOrder = useMemo(
    () => manualSelectedDrafts.map((draft) => draft.componentId),
    [manualSelectedDrafts]
  );
  const manualCardRefs = useRef(new Map());
  const manualCardRefCallbacks = useRef(new Map());
  const draftScrollContainerRef = useRef(null);
  const manualOrderChangeSourceRef = useRef(false);
  const previousManualCountRef = useRef(manualTopicOrder.length);
  const previousManualOrderRef = useRef(manualTopicOrder);

  const draftTopics = useMemo(
    () => mergeDraftTopics(flowStateDraftTopics, manualSelectedDrafts),
    [flowStateDraftTopics, manualSelectedDrafts]
  );
  const hasDraftTopicCards = draftTopics.length > 0;

  const draftFlowData = useMemo(() => buildDraftFlowData(flowState), [flowState]);

  const componentIdsRef = useRef([]);
  const componentIds = useMemo(() => {
    const ids = extractComponentIds(draftTopics);
    const previous = componentIdsRef.current;
    const isSameLength = previous.length === ids.length;
    const isSameOrder = isSameLength && previous.every((id, index) => id === ids[index]);

    if (isSameOrder) {
      return previous;
    }

    componentIdsRef.current = ids;
    return ids;
  }, [draftTopics]);

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(flowState?.components)) return [];
    return flowState.components.filter(
      (componentId) => componentId !== undefined && componentId !== null
    );
  }, [flowState]);

  const enrichedDraftTopics = useMemo(
    () => buildEnrichedDraftTopics(draftTopics, draftDatasetMap),
    [draftDatasetMap, draftTopics]
  );

  const canPersistDraftOrder = Boolean(chatSessionId);
  const canManageManualDrafts = typeof onSelectedTopicsChange === 'function';
  const isDraftActionPending = Boolean(pendingDraftAction);

  const isBuildDisabled = !areRequiredPromptsFilled(prompts, transformedAnswersMap);
  const hasAllRequiredPrompts = !isBuildDisabled;
  const hasPromptSelections = useMemo(() => {
    return Object.values(transformedAnswersMap || {}).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return Object.keys(value).length > 0;
      return Boolean(value);
    });
  }, [transformedAnswersMap]);
  const needsPromptSelection = isViewingDraft && !hasAllRequiredPrompts;
  const isNarrativeProcessing =
    Boolean(flowId) &&
    Boolean(narrativeFlow?.status) &&
    !COMPLETED_OR_ERROR_FLOW_STATUSES.includes(narrativeFlow.status);

  let toolbarActionLabel = 'Select Prompts';
  if (isViewingRun) {
    toolbarActionLabel = 'Reprompt';
  } else if (isViewingDraft && hasAllRequiredPrompts && hasPromptSelections) {
    toolbarActionLabel = 'Create Narrative';
  }

  const promptsUnavailable = !canSelectPrompts && (needsPromptSelection || isViewingRun);
  const isToolbarActionDisabled = promptsUnavailable || isNarrativeProcessing || isBuilding;
  const isToolbarActionLoading = isNarrativeProcessing || isBuilding;
  const shouldShowToolbar = isViewingRun || (isViewingDraft && hasDraftTopicCards);

  const persistTopicOrder = useCallback(
    async (nextIds) => {
      if (!chatSessionId) return null;

      try {
        const updatedFlowState =
          (await updateFlowStateTopicIds({ chatSessionId, topicIds: nextIds })) ||
          (flowState ? { ...flowState, components: nextIds } : { components: nextIds });

        if (onFlowStateChange) {
          onFlowStateChange(updatedFlowState);
        }

        return updatedFlowState;
      } catch (error) {
        console.error('Failed to update topic order', error);
        toast('We could not update your topic order. Please try again.', { status: 'error' });
        throw error;
      }
    },
    [chatSessionId, flowState, onFlowStateChange]
  );

  useEffect(() => {
    if (!flowState || !prompts.length || isPromptModalOpen) return;

    const hydratedAnswers = extractEditablePromptAnswers(flowState);

    setSelectedAnswers(hydratedAnswers);

    const initialTransformedAnswers = prompts.reduce((acc, prompt) => {
      const answers = hydratedAnswers?.[prompt.promptKey];
      const hasAnswers = Array.isArray(answers) ? answers.length > 0 : Boolean(answers);

      if (!hasAnswers) return acc;

      try {
        acc[prompt.promptKey] = transformPromptSelections({ answers, prompt });
      } catch (error) {
        console.error('Failed to transform prompt answers from chat flow state:', error);
      }

      return acc;
    }, {});

    setTransformedAnswersMap(initialTransformedAnswers);

    setActivePrompt((currentPrompt) => {
      if (currentPrompt && prompts.some((prompt) => prompt.promptKey === currentPrompt.promptKey)) {
        return currentPrompt;
      }

      return prompts[0] || null;
    });

    setPromptModalName(flowState?.narrativeName || DEFAULT_PROMPT_MODAL_NAME);
  }, [flowState, isPromptModalOpen, prompts]);

  useEffect(() => {
    setDraftDatasetMap((prev) => pruneDraftDatasetMap(prev, componentIds));
  }, [componentIds]);

  useEffect(() => {
    const previousOrder = previousManualOrderRef.current || [];
    const orderChanged =
      previousOrder.length !== manualTopicOrder.length ||
      previousOrder.some((id, index) => id !== manualTopicOrder[index]);

    if (!orderChanged) return;

    if (!canPersistDraftOrder || !canManageManualDrafts) {
      manualOrderChangeSourceRef.current = false;
      return;
    }

    previousManualOrderRef.current = manualTopicOrder;

    if (manualOrderChangeSourceRef.current) {
      manualOrderChangeSourceRef.current = false;
      return;
    }

    const combinedOrder = mergeUniqueValues(flowStateTopicIds, manualTopicOrder);
    if (!combinedOrder.length) {
      manualOrderChangeSourceRef.current = false;
      return;
    }
    persistTopicOrder(combinedOrder).catch(() => {});
  }, [
    canManageManualDrafts,
    canPersistDraftOrder,
    flowStateTopicIds,
    manualTopicOrder,
    persistTopicOrder,
  ]);

  useEffect(() => {
    const previousCount = previousManualCountRef.current;
    const currentCount = manualTopicOrder.length;
    let rafId = null;
    let rafId2 = null;

    if (currentCount > previousCount) {
      const addedManualId = manualTopicOrder[currentCount - 1];

      rafId = window.requestAnimationFrame(() => {
        rafId2 = window.requestAnimationFrame(() => {
          const manualCardNode = manualCardRefs.current.get(addedManualId);
          const scrollContainerNode = draftScrollContainerRef.current;

          if (!manualCardNode) return;

          if (scrollContainerNode) {
            const manualCardRect = manualCardNode.getBoundingClientRect();
            const scrollContainerRect = scrollContainerNode.getBoundingClientRect();
            const cardIsTallerThanCanvas = manualCardRect.height >= scrollContainerRect.height;

            if (cardIsTallerThanCanvas) {
              const computedStyles = window.getComputedStyle(manualCardNode);
              const marginTop = Number.parseFloat(computedStyles.marginTop) || 0;
              const manualCardTopRelativeToViewport = manualCardRect.top;
              const scrollContainerTopRelativeToViewport = scrollContainerRect.top;
              const nextScrollTop =
                manualCardTopRelativeToViewport -
                scrollContainerTopRelativeToViewport +
                scrollContainerNode.scrollTop -
                marginTop;

              scrollContainerNode.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: nextScrollTop,
              });
              return;
            }
          }

          manualCardNode.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        });
      });
    }

    previousManualCountRef.current = currentCount;

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (rafId2) window.cancelAnimationFrame(rafId2);
    };
  }, [manualTopicOrder]);

  useEffect(() => {
    const idsToFetch = componentIds.filter((id) => !draftDatasetMap[id]);
    if (!idsToFetch.length) return;

    const controller = new AbortController();

    const fetchDraftTopics = async () => {
      try {
        const data = await readDraftTopics({
          componentIds: idsToFetch,
          signal: controller.signal,
        });

        if (data) {
          setDraftDatasetMap((prev) => ({ ...prev, ...data }));
        }
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        console.error('Error fetching draft topics:', error);
      }
    };

    fetchDraftTopics();

    return () => {
      controller.abort();
    };
  }, [componentIds, draftDatasetMap]);

  const handlePromptModalVisibilityChange = (nextIsOpen) => {
    if (!nextIsOpen) {
      if (!shouldPersistPromptChangesRef.current && promptSelectionsSnapshotRef.current) {
        const {
          name,
          selectedAnswers: answersSnapshot,
          transformedAnswersMap: transformedAnswersSnapshot,
        } = promptSelectionsSnapshotRef.current;

        setPromptModalName(name);
        setSelectedAnswers(answersSnapshot);
        setTransformedAnswersMap(transformedAnswersSnapshot);
      }

      shouldPersistPromptChangesRef.current = false;
      promptSelectionsSnapshotRef.current = null;
    }

    setIsPromptModalOpen(nextIsOpen);
  };

  const handleSelectPrompts = () => {
    if (!canSelectPrompts) return;

    setBuildError(null);
    promptSelectionsSnapshotRef.current = {
      name: promptModalName,
      selectedAnswers,
      transformedAnswersMap,
    };
    shouldPersistPromptChangesRef.current = false;
    setIsPromptModalOpen(true);
  };

  const handleBuildFromPrompts = async () => {
    if (isBuildDisabled || isBuilding) return;

    const promptAnswers = buildPromptAnswersPayload(
      transformedAnswersMap,
      flowState?.readOnlyPromptAnswers
    );

    if (!Object.keys(promptAnswers).length) {
      const errorMessage = 'Please complete the required prompts before building your Narrative.';
      setBuildError(errorMessage);
      toast(errorMessage, { status: 'error' });
      return;
    }

    setBuildError(null);
    setIsBuilding(true);

    try {
      const createdNarrative = await createNarrative({
        flowId,
        id: 'dynamic',
        promptAnswers,
        title: promptModalName?.trim() || DEFAULT_PROMPT_MODAL_NAME,
        topicIds: componentIds,
      });

      if (!createdNarrative?.flowId) {
        throw new Error('Narrative build did not return a flow identifier.');
      }

      if (onFlowSelected) {
        onFlowSelected(createdNarrative.flowId);
      }

      shouldPersistPromptChangesRef.current = true;
      handlePromptModalVisibilityChange(false);
    } catch (error) {
      const promptErrors = error?.response?.data?.['Prompt Errors'] ?? [];
      const errorMessage =
        promptErrors[0] || 'There was an error building your Narrative. Please try again.';

      setBuildError(errorMessage);
      toast(errorMessage, { status: 'error' });
      console.error('Failed to build Narrative:', error);
    } finally {
      setIsBuilding(false);
    }
  };

  const registerManualCardRef = useCallback((componentId) => {
    if (componentId == null) return null;

    if (!manualCardRefCallbacks.current.has(componentId)) {
      manualCardRefCallbacks.current.set(componentId, (node) => {
        if (node) {
          manualCardRefs.current.set(componentId, node);
        } else {
          manualCardRefs.current.delete(componentId);
          manualCardRefCallbacks.current.delete(componentId);
        }
      });
    }

    return manualCardRefCallbacks.current.get(componentId);
  }, []);

  const handleToolbarAction = () => {
    if (isBuilding) return;

    if (isViewingDraft && hasAllRequiredPrompts) {
      handleBuildFromPrompts();
      return;
    }

    handleSelectPrompts();
  };

  const handleDraftTopicAction = useCallback(
    async (componentId, action) => {
      if (!canPersistDraftOrder || !componentId) return;

      const currentIds = flowStateTopicIds;
      const currentIndex = currentIds.indexOf(componentId);
      if (currentIndex === -1) return;

      const nextIds = [...currentIds];

      if (action === 'up' && currentIndex > 0) {
        [nextIds[currentIndex - 1], nextIds[currentIndex]] = [
          nextIds[currentIndex],
          nextIds[currentIndex - 1],
        ];
      } else if (action === 'down' && currentIndex < nextIds.length - 1) {
        [nextIds[currentIndex + 1], nextIds[currentIndex]] = [
          nextIds[currentIndex],
          nextIds[currentIndex + 1],
        ];
      } else if (action === 'remove') {
        nextIds.splice(currentIndex, 1);
      } else {
        return;
      }

      const noChange =
        nextIds.length === currentIds.length &&
        nextIds.every((id, index) => id === currentIds[index]);
      if (noChange) return;

      setPendingDraftAction({ action, componentId });
      try {
        await persistTopicOrder(nextIds);
        if (action === 'remove' && typeof onSelectedTopicsChange === 'function') {
          manualOrderChangeSourceRef.current = true;
          onSelectedTopicsChange((previousTopics) => {
            if (!Array.isArray(previousTopics)) return previousTopics;
            return previousTopics.filter((topic) => topic?.id !== componentId);
          });
        }
      } catch (error) {
        console.error('Failed to update draft topic order', error);
        // toast already shown inside persistTopicOrder
      } finally {
        setPendingDraftAction(null);
      }
    },
    [canPersistDraftOrder, flowStateTopicIds, onSelectedTopicsChange, persistTopicOrder]
  );

  const handleManualTopicAction = useCallback(
    (componentId, action) => {
      if (typeof onSelectedTopicsChange !== 'function' || !componentId) return;

      let nextManualTopics = null;
      manualOrderChangeSourceRef.current = true;

      onSelectedTopicsChange((prevTopics) => {
        if (!Array.isArray(prevTopics)) return prevTopics;
        const index = prevTopics.findIndex((topic) => topic?.id === componentId);
        if (index === -1) return prevTopics;

        const nextTopics = [...prevTopics];

        if (action === 'remove') {
          nextTopics.splice(index, 1);
        } else if (action === 'up' && index > 0) {
          [nextTopics[index - 1], nextTopics[index]] = [nextTopics[index], nextTopics[index - 1]];
        } else if (action === 'down' && index < nextTopics.length - 1) {
          [nextTopics[index + 1], nextTopics[index]] = [nextTopics[index], nextTopics[index + 1]];
        } else {
          manualOrderChangeSourceRef.current = false;
          return prevTopics;
        }

        nextManualTopics = nextTopics;
        return nextTopics;
      });

      if (nextManualTopics) {
        const manualIds = nextManualTopics.map((topic) => topic.id);
        const combinedOrder = mergeUniqueValues(flowStateTopicIds, manualIds);
        if (!combinedOrder.length) return;
        persistTopicOrder(combinedOrder)
          .catch(() => {})
          .finally(() => {
            manualOrderChangeSourceRef.current = false;
          });
      } else {
        manualOrderChangeSourceRef.current = false;
      }
    },
    [flowStateTopicIds, onSelectedTopicsChange, persistTopicOrder]
  );

  const handleRunTopicAction = useCallback(
    async ({ action, flowTopics = [], setFlow, topic }) => {
      if (
        !isViewingRun ||
        !topic?.id ||
        typeof setFlow !== 'function' ||
        isNarrativeProcessing ||
        Boolean(pendingRunTopicAction)
      )
        return;

      const flowTopicId = topic.id;
      const componentId = topic.topicId;
      const topicsList = Array.isArray(flowTopics) ? flowTopics : [];
      const originalTopics = topicsList.slice();
      const applyTopics = (nextTopics) => {
        setFlow((prevFlow) => {
          if (!prevFlow) return prevFlow;
          return { ...prevFlow, topics: nextTopics };
        });
      };

      setPendingRunTopicAction({ action, topicId: flowTopicId });

      try {
        if ((action === 'up' || action === 'down') && flowId) {
          const currentIndex = topicsList.findIndex((item) => item?.id === flowTopicId);
          if (currentIndex === -1) return;
          const destinationIndex = action === 'up' ? currentIndex - 1 : currentIndex + 1;
          if (destinationIndex < 0 || destinationIndex >= topicsList.length) return;

          const nextTopics = topicsList.slice();
          [nextTopics[currentIndex], nextTopics[destinationIndex]] = [
            nextTopics[destinationIndex],
            nextTopics[currentIndex],
          ];
          applyTopics(nextTopics);
          await reorderFlowTopics({
            flowId,
            topicIds: nextTopics.map((item) => item.id),
          });
        } else if (action === 'exclude' || action === 'include') {
          const shouldHide = action === 'exclude';
          const alreadyMatches = topic.isHidden === shouldHide;
          if (alreadyMatches) return;

          const nextTopics = topicsList.map((item) =>
            item.id === flowTopicId ? { ...item, isHidden: shouldHide } : item
          );
          applyTopics(nextTopics);
          await updateTopicVisibility({ id: flowTopicId, isHidden: shouldHide });
        } else if (action === 'delete') {
          const nextTopics = topicsList.filter((item) => item.id !== flowTopicId);
          applyTopics(nextTopics);
          await softDeleteFlowTopic({ topicId: flowTopicId });

          if (chatSessionId && componentId != null) {
            const currentComponentIds = Array.isArray(flowState?.components)
              ? flowState.components
              : [];
            const nextComponentIds = currentComponentIds.filter(
              (id) => String(id) !== String(componentId)
            );

            if (nextComponentIds.length !== currentComponentIds.length) {
              const optimisticFlowState = flowState
                ? { ...flowState, components: nextComponentIds }
                : { components: nextComponentIds };

              try {
                const updatedFlowState =
                  (await updateFlowStateTopicIds({ chatSessionId, topicIds: nextComponentIds })) ||
                  optimisticFlowState;

                if (onFlowStateChange) {
                  onFlowStateChange(updatedFlowState);
                }
              } catch (syncError) {
                console.error('Failed to sync flow state after topic deletion', syncError);
                if (onFlowStateChange) {
                  onFlowStateChange(optimisticFlowState);
                }
                toast('We could not sync the chat state. Please refresh and try again.', {
                  status: 'error',
                });
              }
            }
          }
        } else {
          return;
        }
      } catch (error) {
        console.error('Failed to process run topic action', error);
        toast('We could not complete that action. Please try again.', { status: 'error' });
        applyTopics(originalTopics);
      } finally {
        setPendingRunTopicAction(null);
        if (action === 'delete') {
          setTopicPendingDeletion(null);
        }
      }
    },
    [
      chatSessionId,
      flowId,
      flowState,
      isNarrativeProcessing,
      isViewingRun,
      onFlowStateChange,
      pendingRunTopicAction,
      setTopicPendingDeletion,
    ]
  );

  const runTopicControlsBuilder = useCallback(
    ({ index, setFlow, topic, topics }) => {
      if (!isViewingRun || !topic?.id || !Array.isArray(topics)) return null;

      const totalTopics = topics.length;
      const isExcluded = Boolean(topic?.isHidden);
      const isPending = Boolean(pendingRunTopicAction);
      const shouldDisableRunControls = isPending || isNarrativeProcessing;

      const controls = {
        primaryAction: {
          disabled: shouldDisableRunControls,
          label: isExcluded ? 'Include' : 'Exclude',
          onClick: () =>
            handleRunTopicAction({
              action: isExcluded ? 'include' : 'exclude',
              flowTopics: topics,
              setFlow,
              topic,
            }),
        },
        secondaryActions: canManageRunTopics
          ? [
              {
                disabled: shouldDisableRunControls || totalTopics < 2 || index <= 0,
                label: 'Move Up',
                onClick: () =>
                  handleRunTopicAction({
                    action: 'up',
                    flowTopics: topics,
                    setFlow,
                    topic,
                  }),
              },
              {
                disabled: shouldDisableRunControls || totalTopics < 2 || index === totalTopics - 1,
                label: 'Move Down',
                onClick: () =>
                  handleRunTopicAction({
                    action: 'down',
                    flowTopics: topics,
                    setFlow,
                    topic,
                  }),
              },
            ]
          : [],
      };

      if (isExcluded && canManageRunTopics) {
        controls.secondaryActions.unshift({
          disabled: shouldDisableRunControls,
          label: 'Remove',
          onClick: () =>
            setTopicPendingDeletion({
              flowTopics: topics,
              setFlow,
              topic,
            }),
        });
      }

      return controls;
    },
    [
      canManageRunTopics,
      handleRunTopicAction,
      isNarrativeProcessing,
      isViewingRun,
      pendingRunTopicAction,
      setTopicPendingDeletion,
    ]
  );

  const preBuildModal = canSelectPrompts ? (
    <PreBuildModal
      activePrompt={activePrompt}
      buildError={buildError}
      getPillTooltip={getPromptPillTooltip}
      getPillValue={getPromptPillValue}
      handleBuild={handleBuildFromPrompts}
      headerIcon={null}
      isBuildDisabled={isBuildDisabled}
      isBuilding={isBuilding}
      isLoading={false}
      isModalOpen={isPromptModalOpen}
      name={promptModalName}
      prompts={prompts}
      routeLabel={DEFAULT_PROMPT_ROUTE_LABEL}
      sameLevelPrompts={[]}
      searchPrompts={[]}
      selectedAnswers={selectedAnswers}
      setActivePrompt={setActivePrompt}
      setIsModalOpen={handlePromptModalVisibilityChange}
      setName={setPromptModalName}
      setSelectedAnswers={setSelectedAnswers}
      setTransformedAnswersMap={setTransformedAnswersMap}
      transformedAnswersMap={transformedAnswersMap}
      transformPromptSelections={transformPromptSelections}
      type="Narrative"
    />
  ) : null;

  const deleteConfirmationModal = topicPendingDeletion ? (
    <Modal
      buttons={[
        <Button
          key="cancel-delete-topic"
          onClick={() => setTopicPendingDeletion(null)}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          isDisabled={
            pendingRunTopicAction?.action === 'delete' &&
            pendingRunTopicAction?.topicId === topicPendingDeletion?.topic?.id
          }
          key="confirm-delete-topic"
          onClick={() =>
            handleRunTopicAction({
              ...topicPendingDeletion,
              action: 'delete',
            })
          }
          text="Remove"
          variant="error"
        />,
      ]}
      isActive={Boolean(topicPendingDeletion)}
      onClose={() => setTopicPendingDeletion(null)}
      size="small"
      title="Remove"
    >
      <p>Are you sure you would like to remove this topic card? You&apos;ll lose its results.</p>
    </Modal>
  ) : null;

  if (isViewingRun) {
    return (
      <div className={styles['narratives-canvas-container']} ref={draftScrollContainerRef}>
        <div className={styles['narratives-canvas-content']}>
          <NarrativeFlowView
            flowId={flowId}
            getTopicControls={runTopicControlsBuilder}
            hidePageTitle={true}
            useWindowScroll={false}
          />
        </div>
        <Footer />
        {shouldShowToolbar && (
          <CanvasFloatingToolbar
            onSelectPrompts={handleToolbarAction}
            selectPromptsDisabled={isToolbarActionDisabled}
            selectPromptsLabel={toolbarActionLabel}
            selectPromptsLoading={isToolbarActionLoading}
          />
        )}
        {preBuildModal}
        {deleteConfirmationModal}
      </div>
    );
  }

  return (
    <div className={styles['narratives-canvas-container']} ref={draftScrollContainerRef}>
      <div className={styles['narratives-canvas-content']}>
        {draftTopics.length > 0 && (
          <div className={styles['narratives-canvas-draft-view']}>
            {draftFlowData && (
              <NarrativeTitle
                downloadData={EMPTY_DOWNLOAD_MAP}
                flowData={draftFlowData}
                handleReprompt={() => {}}
                isDraft
                setFlow={() => {}}
              />
            )}

            <motion.div className={styles['narratives-canvas-draft-grid']} layout>
              <AnimatePresence initial={false} mode="popLayout">
                {enrichedDraftTopics.map((draft, index) => {
                  const flowIndex = flowStateTopicIds.indexOf(draft.componentId);
                  const isFlowStateDraft = flowIndex > -1;
                  const topicInManualOrder = manualTopicOrder.indexOf(draft.componentId);
                  const isManualDraft = !isFlowStateDraft && topicInManualOrder > -1;
                  let topicControls = null;

                  if (isFlowStateDraft && canPersistDraftOrder) {
                    topicControls = {
                      primaryAction: {
                        disabled: isDraftActionPending,
                        label: 'Remove',
                        onClick: () => handleDraftTopicAction(draft.componentId, 'remove'),
                      },
                      secondaryActions: [
                        {
                          disabled:
                            isDraftActionPending || flowIndex <= 0 || flowStateTopicIds.length < 2,
                          label: 'Move Up',
                          onClick: () => handleDraftTopicAction(draft.componentId, 'up'),
                        },
                        {
                          disabled:
                            isDraftActionPending ||
                            flowIndex === -1 ||
                            flowIndex === flowStateTopicIds.length - 1 ||
                            flowStateTopicIds.length < 2,
                          label: 'Move Down',
                          onClick: () => handleDraftTopicAction(draft.componentId, 'down'),
                        },
                      ],
                    };
                  } else if (
                    !isFlowStateDraft &&
                    canManageManualDrafts &&
                    topicInManualOrder > -1
                  ) {
                    const manualCount = manualTopicOrder.length;
                    topicControls = {
                      primaryAction: {
                        disabled: false,
                        label: 'Remove',
                        onClick: () => handleManualTopicAction(draft.componentId, 'remove'),
                      },
                      secondaryActions: [
                        {
                          disabled: manualCount < 2 || topicInManualOrder <= 0,
                          label: 'Move Up',
                          onClick: () => handleManualTopicAction(draft.componentId, 'up'),
                        },
                        {
                          disabled: manualCount < 2 || topicInManualOrder === manualCount - 1,
                          label: 'Move Down',
                          onClick: () => handleManualTopicAction(draft.componentId, 'down'),
                        },
                      ],
                    };
                  }

                  return (
                    <motion.div
                      className={styles['narratives-canvas-draft-card']}
                      key={`draft-topic-${draft.componentId}`}
                      layout
                      layoutId={`draft-topic-${draft.componentId}`}
                      {...TOPIC_CARD_MOTION_PROPS}
                    >
                      <div ref={isManualDraft ? registerManualCardRef(draft.componentId) : null}>
                        <NarrativeTopicCard
                          initialTopicData={buildDraftTopicData(draft)}
                          isDraftCard={true}
                          keyIndex={index}
                          onPreview={() => {}}
                          setDownloadData={() => {}}
                          topic={buildTopicShell(draft)}
                          topicControls={topicControls}
                          useWindowScroll={false}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </div>

      <Footer />
      {shouldShowToolbar && (
        <CanvasFloatingToolbar
          onSelectPrompts={handleToolbarAction}
          selectPromptsDisabled={isToolbarActionDisabled}
          selectPromptsLabel={toolbarActionLabel}
          selectPromptsLoading={isToolbarActionLoading}
        />
      )}
      {preBuildModal}
      {deleteConfirmationModal}
    </div>
  );
};

NarrativesCanvas.propTypes = {
  chatSessionId: PropTypes.string,
  flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowState: PropTypes.shape({
    components: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    narrativeName: PropTypes.string,
    readOnlyPromptAnswers: PropTypes.object,
  }),
  onFlowSelected: PropTypes.func,
  onFlowStateChange: PropTypes.func,
  onSelectedTopicsChange: PropTypes.func,
  selectedTopicCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      vizType: PropTypes.string,
    })
  ),
};

export default NarrativesCanvas;
